// Import general mixin for shared functionality.
import defaultApp from './defaultApp.js';

// Import general functions for shared functionality.
import { getCookie, setCookie, deleteCookie } from '../functions.js';

// Import components for this specific app.
import StoryItem from './components/StoryItem.vue';
import LeafletMap from './components/LeafletMap.vue';

const storiesApp = {
    ...defaultApp,
    // Overwrite default app functionalities as you please by using the spread operator.
    components: {
        ...defaultApp.components,
        StoryItem,
        LeafletMap
    },
    data() {
        return {
            ...defaultApp.data(),
            blockView: true,
        }
    },
    watch: {
        ...defaultApp.watch,
        blockView(newView) {
            if (!newView) {
                setCookie(this.constants.cookies.blockMapView, 'false', 365);
                this.$nextTick(() => {
                    const mapComponent = this.$refs.leafletMap;
                    if (mapComponent && mapComponent.$refs.map) mapComponent.$refs.map.leafletObject.invalidateSize();
                });
            } else {
                deleteCookie(this.constants.cookies.blockMapView);
            }

            this.dataLimit = newView && !this.dataWidget
                ? (this.$el.dataset.limit ? parseInt(this.$el.dataset.limit) : 15)
                : 9999;

            this.fetchInitialData();
        },
    },
    methods: {
        ...defaultApp.methods,
        async init() {
            // Set data props according to data attributes in HTML template.
            if (this.$el.dataset.widget) this.dataWidget = true;
            if (this.$el.dataset.themeId) this.dataThemeId = this.$el.dataset.themeId;
            if (this.$el.dataset.regionId) this.dataRegionId = this.$el.dataset.regionId;
            if (this.$el.dataset.authorId) this.dataAuthorId = this.$el.dataset.authorId;
            if (this.$el.dataset.periodId) this.dataPeriodId = this.$el.dataset.periodId;
            if (this.$el.dataset.organizationId) this.dataOrganizationId = this.$el.dataset.organizationId;
            if (this.$el.dataset.overruleIds) this.dataOverruleIds = this.$el.dataset.overruleIds.split(',');
            if (this.$el.dataset.culture) this.dataCulture = this.$el.dataset.culture;
            if (this.$el.dataset.api) this.dataApi = this.$el.dataset.api + '?culture=' + this.dataCulture;
            if (this.$el.dataset.limit) this.dataLimit = parseInt(this.$el.dataset.limit);
            if (!this.blockView && !this.dataWidget) this.dataLimit = 9999;
            if (this.$el.dataset.sortKey) this.dataSortKey = this.$el.dataset.sortKey;
            if (this.$el.dataset.sortDirection) this.dataSortDirection = this.$el.dataset.sortDirection;
            if (this.$el.dataset.amountsSentence) this.dataAmountsSentence = this.$el.dataset.amountsSentence;

            // Construct query model and alter data props based on URL search params.
            const urlParams = new URLSearchParams(window.location.search);

            let pageParam = urlParams.has(this.constants.queryParams.page) ? parseInt(urlParams.get(this.constants.queryParams.page)) : 0;
            if (pageParam > this.pageLimit) pageParam = this.pageLimit;
            if (pageParam) urlParams.delete(this.constants.queryParams.page);
            if (pageParam < 2) pageParam = 0;
            this.searchText = urlParams.has(this.constants.queryParams.text) ? urlParams.get(this.constants.queryParams.text) : '';
            if (this.searchText) urlParams.delete(this.constants.queryParams.text);
            let filterParams = urlParams.toString() ? urlParams : null;
            this.filterParams = filterParams;

            // Prefetch data for overrule ids.
            if (this.dataOverruleIds.length) {
                const overruleResponse = await this.fetchData();
                // Make sure ordering corresponds to picking order in CMS.
                if (overruleResponse && overruleResponse.data && overruleResponse.data.items && overruleResponse.data.items.length) {
                    overruleResponse.data.items = overruleResponse.data.items.sort((a, b) => this.dataOverruleIds.indexOf(a.id) - this.dataOverruleIds.indexOf(b.id));
                }
                this.setData(overruleResponse);
                this.dataOverruleIds = [];
            }

            let tempLimit = this.dataLimit;
            if (pageParam && !this.dataWidget) {
                this.page = pageParam;
                this.dataLimit = this.dataLimit * this.page;
            }

            // Initial data fetch.
            const response = await this.fetchData();
            let replace = !this.itemsSet;
            this.setData(response, replace);
            this.dataLimit = tempLimit;
            this.filterParams = null;

            // Remove duplicates from items.
            if (this.itemsSet) {
                this.data.items = this.removeDuplicatesById(this.data.items);
                if (this.dataWidget) this.data.items = this.data.items.slice(0, this.dataLimit);
            }

            this.$nextTick(() => {
                this.initSwiperIfNeeded();
            });
        },
        getMode(index) {
            switch (true) {
                case index % 4 === 3:
                    return this.constants.images.modes.fullwidth;
                case index % 2 === 1:
                    return this.constants.images.modes.image;
                default:
                    return this.constants.images.modes.normal;
            }
        }
    },
    created() {
        // Check which view is set as cookie.
        if (getCookie(this.constants.cookies.blockMapView) !== '') this.blockView = false;
        // Data alteration function for reloading data with textual search input.
        this.changeSearchText = this.debounce(() => {
            this.fetchInitialData();
        });
        // Initialize the debounced loading function.
        this.debounceSetLoading = this.createDebouncedLoading();
    },
};

export default storiesApp;
